import { useEffect, useState } from "react";
import { CalendarClock, Loader2, Pencil, RotateCw, UserPlus, Video, X } from "lucide-react";
import {
  cancelChatMeeting,
  CHAT_MEETING_REMIND_DEFAULT,
  formatMeetingWhen,
  meetingDurationMin,
  meetingRemindLabel,
  meetingStartsToLocal,
  normalizeMeetingRemindMinutes,
  scheduleChatMeeting,
  type ChatMeeting,
} from "@/lib/meetingsApi";
import {
  copyText,
  createChatGuestInvite,
  dedupeChatGuests,
  guestInviteListTitle,
  guestInvitePresenceLabel,
  reinviteChatGuest,
  revokeChatGuestInvite,
  type ChatGuestInvite,
} from "@/lib/guestsApi";

export type ChatMeetingMemberOpt = { id: number; name: string };

type Props = {
  suiteUserId: string;
  conversationId: number;
  meetings: ChatMeeting[];
  members: ChatMeetingMemberOpt[];
  selfId: number;
  busy?: boolean;
  onRefresh: () => void;
  onJoin: () => void;
  draftStartsAt: string;
  onDraftStartsAt: (value: string) => void;
  draftDuration: number;
  onDraftDuration: (value: number) => void;
  draftTitle: string;
  onDraftTitle: (value: string) => void;
  guests?: ChatGuestInvite[];
  onRefreshGuests?: () => void;
};

export function ChatGuestRows({
  guests,
  locked,
  revokeBusyId,
  reinviteBusyId,
  onRevoke,
  onReinvite,
}: {
  guests: ChatGuestInvite[];
  locked?: boolean;
  revokeBusyId?: number | null;
  reinviteBusyId?: number | null;
  onRevoke: (id: number) => void;
  onReinvite?: (g: ChatGuestInvite) => void;
}) {
  const rows = dedupeChatGuests(guests);
  if (rows.length === 0) return null;
  return (
    <ul className="space-y-1">
      {rows.slice(0, 8).map((g) => (
        <li
          key={g.id}
          className="flex items-center gap-1.5 rounded-lg bg-[var(--chat-bg)] px-2 py-1.5 text-xs"
        >
          <span className="min-w-0 flex-1 truncate">
            {guestInviteListTitle(g)}
            <span className="text-[var(--chat-muted)]">
              {" · "}
              {guestInvitePresenceLabel(g)}
            </span>
          </span>
          {onReinvite ? (
            <button
              type="button"
              className="shrink-0 rounded px-1.5 py-1 text-[11px] font-medium text-[var(--chat-accent-strong)] hover:bg-[var(--chat-accent-soft)] disabled:opacity-40"
              disabled={locked}
              title="Relancer l’invitation — nouveau lien, l’ancien reste mort"
              onClick={() => onReinvite(g)}
            >
              {reinviteBusyId === g.id ? (
                <Loader2 className="size-3 animate-spin" />
              ) : (
                <span className="inline-flex items-center gap-0.5">
                  <RotateCw className="size-3" />
                  Relancer
                </span>
              )}
            </button>
          ) : null}
          <button
            type="button"
            className="shrink-0 rounded p-1 text-[var(--chat-muted)] hover:bg-[var(--chat-danger)]/10 hover:text-[var(--chat-danger)] disabled:opacity-40"
            disabled={locked}
            title="Retirer l’invitation"
            onClick={() => onRevoke(g.id)}
          >
            {revokeBusyId === g.id ? <Loader2 className="size-3 animate-spin" /> : <X className="size-3" />}
          </button>
        </li>
      ))}
    </ul>
  );
}

export function ChatMeetingSection({
  suiteUserId,
  conversationId,
  meetings,
  members,
  selfId,
  busy,
  onRefresh,
  onJoin,
  draftStartsAt,
  onDraftStartsAt,
  draftDuration,
  onDraftDuration,
  draftTitle,
  onDraftTitle,
  guests = [],
  onRefreshGuests,
}: Props) {
  const meeting = meetings[0] ?? null;
  const memberIdsKey = members.map((m) => m.id).join(",");
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [editing, setEditing] = useState(!meeting);
  const [hint, setHint] = useState<string | null>(null);
  const [guestEmail, setGuestEmail] = useState("");
  const [guestBusy, setGuestBusy] = useState(false);
  const [revokeBusyId, setRevokeBusyId] = useState<number | null>(null);
  const [reinviteBusyId, setReinviteBusyId] = useState<number | null>(null);
  const [draftRemind, setDraftRemind] = useState(CHAT_MEETING_REMIND_DEFAULT);
  const [draftAttendees, setDraftAttendees] = useState<number[]>(() => members.map((m) => m.id));

  const locked = Boolean(
    busy || saving || cancelling || guestBusy || revokeBusyId != null || reinviteBusyId != null
  );

  useEffect(() => {
    setEditing(!meeting);
  }, [meeting?.id]);

  useEffect(() => {
    if (meeting) return;
    const ids = memberIdsKey
      .split(",")
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (!ids.includes(selfId) && selfId > 0) ids.push(selfId);
    setDraftAttendees(ids);
  }, [meeting, memberIdsKey, selfId]);

  useEffect(() => {
    if (!meeting) return;
    onDraftStartsAt(meetingStartsToLocal(meeting.starts_at));
    onDraftDuration(meetingDurationMin(meeting.starts_at, meeting.ends_at));
    onDraftTitle(meeting.title.trim());
    setDraftRemind(normalizeMeetingRemindMinutes(meeting.remind_minutes));
    const invited = meeting.attendee_ids.filter((id) =>
      memberIdsKey.split(",").includes(String(id))
    );
    const all = memberIdsKey
      .split(",")
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n) && n > 0);
    const next = invited.length > 0 ? invited : all;
    if (!next.includes(selfId)) next.push(selfId);
    setDraftAttendees(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync depuis le serveur uniquement
  }, [
    meeting?.id,
    meeting?.starts_at,
    meeting?.ends_at,
    meeting?.title,
    meeting?.remind_minutes,
    meeting?.attendee_ids?.join(",") ?? "",
    memberIdsKey,
    selfId,
  ]);

  const submit = async () => {
    if (locked) return;
    setSaving(true);
    setHint(null);
    const titleToSend = draftTitle.trim() || meeting?.title?.trim() || "";
    const attendees = draftAttendees.includes(selfId)
      ? draftAttendees
      : [...draftAttendees, selfId];
    const r = await scheduleChatMeeting(
      suiteUserId,
      conversationId,
      draftStartsAt,
      draftDuration,
      titleToSend,
      draftRemind,
      attendees
    );
    setSaving(false);
    if (!r.success || !r.data) {
      setHint(r.message || "Impossible d’enregistrer.");
      return;
    }
    const mailed = r.data.mailed ?? 0;
    setHint(
      r.data.updated
        ? `Créneau mis à jour${mailed ? ` · ${mailed} e-mail${mailed > 1 ? "s" : ""}` : ""}`
        : `Planifiée${mailed ? ` · ${mailed} e-mail${mailed > 1 ? "s" : ""}` : ""}`
    );
    setEditing(false);
    onRefresh();
    onRefreshGuests?.();
  };

  const cancelOne = async () => {
    if (locked || !meeting) return;
    setCancelling(true);
    setHint(null);
    const r = await cancelChatMeeting(suiteUserId, conversationId, meeting.id);
    setCancelling(false);
    if (!r.success) {
      setHint(r.message || "Impossible d’annuler.");
      return;
    }
    setHint("Visio annulée — agenda et invitations mis à jour.");
    setEditing(true);
    onRefresh();
    onRefreshGuests?.();
  };

  const inviteGuest = async () => {
    if (locked || !meeting) return;
    const mail = guestEmail.trim();
    if (!mail || !mail.includes("@")) {
      setHint("Indiquez un e-mail pour l’invité.");
      return;
    }
    setGuestBusy(true);
    setHint(null);
    const r = await createChatGuestInvite(suiteUserId, {
      conversationId,
      meetingId: meeting.id,
      email: mail,
      startsAt: meeting.starts_at,
    });
    setGuestBusy(false);
    if (!r.success || !r.data) {
      setHint(r.message || "Impossible d’inviter.");
      return;
    }
    if (r.data.join_url) await copyText(r.data.join_url);
    setGuestEmail("");
    setHint(r.data.mailed ? "Invitation envoyée — lien copié." : "Lien copié (e-mail non envoyé).");
    onRefreshGuests?.();
  };

  const copyMeetingLink = async () => {
    if (locked || !meeting) return;
    setGuestBusy(true);
    setHint(null);
    const r = await createChatGuestInvite(suiteUserId, {
      conversationId,
      meetingId: meeting.id,
      startsAt: meeting.starts_at,
    });
    setGuestBusy(false);
    if (!r.success || !r.data) {
      setHint(r.message || "Impossible de créer le lien.");
      return;
    }
    if (r.data.join_url) await copyText(r.data.join_url);
    setHint(`Lien copié · ${formatMeetingWhen(meeting.starts_at)}`);
    onRefreshGuests?.();
  };

  const revokeGuest = async (inviteId: number) => {
    if (locked) return;
    setRevokeBusyId(inviteId);
    const r = await revokeChatGuestInvite(suiteUserId, conversationId, inviteId);
    setRevokeBusyId(null);
    if (!r.success) {
      setHint(r.message || "Impossible de retirer.");
      return;
    }
    onRefreshGuests?.();
  };

  const relaunchGuest = async (inviteId: number) => {
    if (locked) return;
    setReinviteBusyId(inviteId);
    setHint(null);
    const r = await reinviteChatGuest(suiteUserId, conversationId, inviteId);
    setReinviteBusyId(null);
    if (!r.success || !r.data) {
      setHint(r.message || "Impossible de relancer.");
      return;
    }
    if (r.data.join_url) await copyText(r.data.join_url);
    setHint(r.data.mailed ? "Invitation relancée — e-mail envoyé, lien copié." : "Nouveau lien copié.");
    onRefreshGuests?.();
  };

  const showForm = !meeting || editing;

  return (
    <div className="space-y-3 rounded-xl border border-[var(--chat-border)] p-3">
      <p className="flex items-center gap-1.5 text-xs font-semibold text-[var(--chat-muted)]">
        <CalendarClock className="size-3.5" />
        Visio planifiée
      </p>

      {meeting ? (
        <div className="rounded-lg bg-[var(--chat-surface-elevated)] px-2.5 py-2 text-sm">
          <p className="font-medium">{meeting.title}</p>
          <p className="text-xs text-[var(--chat-muted)]">{formatMeetingWhen(meeting.starts_at)}</p>
          {meeting.attendee_ids.length > 0 ? (
            <p className="text-[11px] text-[var(--chat-muted)]">
              {meeting.attendee_ids.length} participant
              {meeting.attendee_ids.length > 1 ? "s" : ""} Suite
            </p>
          ) : null}
          <p className="text-[11px] text-[var(--chat-muted)]">
            {meeting.remind_minutes > 0
              ? `Rappel ${meetingRemindLabel(meeting.remind_minutes)}`
              : "Pas de rappel"}
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {meeting.joinable ? (
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-lg bg-[var(--chat-accent-soft)] px-2 py-1 text-xs font-medium text-[var(--chat-accent-strong)] disabled:opacity-50"
                disabled={locked}
                onClick={onJoin}
              >
                <Video className="size-3" />
                Rejoindre
              </button>
            ) : null}
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-[var(--chat-accent-strong)] hover:bg-[var(--chat-accent-soft)] disabled:opacity-50"
              disabled={locked}
              onClick={() => setEditing((v) => !v)}
            >
              <Pencil className="size-3" />
              {editing ? "Fermer" : "Modifier l’heure"}
            </button>
            {meeting.can_cancel ? (
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-[var(--chat-danger)] hover:bg-[var(--chat-danger)]/10 disabled:opacity-50"
                disabled={locked}
                onClick={() => void cancelOne()}
              >
                {cancelling ? <Loader2 className="size-3 animate-spin" aria-hidden /> : null}
                {cancelling ? "Annulation…" : "Annuler"}
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <p className="text-xs text-[var(--chat-muted)]">
          Choisissez une date, puis invitez par e-mail. Un seul créneau par salle.
        </p>
      )}

      {showForm ? (
        <div className="space-y-2">
          <label className="block space-y-1">
            <span className="text-xs text-[var(--chat-muted)]">Titre de la visio</span>
            <input
              value={draftTitle}
              onChange={(e) => onDraftTitle(e.target.value)}
              placeholder="Ex. Réunion district"
              disabled={locked}
              className="w-full rounded-xl border border-[var(--chat-border)] bg-[var(--chat-bg)] px-3 py-2 text-sm outline-none focus:border-[var(--chat-accent)] disabled:opacity-50"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-xs text-[var(--chat-muted)]">Date et heure</span>
            <input
              type="datetime-local"
              value={draftStartsAt}
              onChange={(e) => onDraftStartsAt(e.target.value)}
              disabled={locked}
              className="w-full rounded-xl border border-[var(--chat-border)] bg-[var(--chat-bg)] px-3 py-2 text-sm outline-none focus:border-[var(--chat-accent)] disabled:opacity-50"
            />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block space-y-1">
              <span className="text-xs text-[var(--chat-muted)]">Durée</span>
              <select
                value={draftDuration}
                onChange={(e) => onDraftDuration(Number(e.target.value))}
                disabled={locked}
                className="w-full rounded-xl border border-[var(--chat-border)] bg-[var(--chat-bg)] px-3 py-2 text-sm outline-none focus:border-[var(--chat-accent)] disabled:opacity-50"
              >
                <option value={30}>30 min</option>
                <option value={45}>45 min</option>
                <option value={60}>1 h</option>
                <option value={90}>1 h 30</option>
                <option value={120}>2 h</option>
              </select>
            </label>
            <label className="block space-y-1">
              <span className="text-xs text-[var(--chat-muted)]">Rappel</span>
              <select
                value={draftRemind}
                onChange={(e) => setDraftRemind(normalizeMeetingRemindMinutes(Number(e.target.value)))}
                disabled={locked}
                className="w-full rounded-xl border border-[var(--chat-border)] bg-[var(--chat-bg)] px-3 py-2 text-sm outline-none focus:border-[var(--chat-accent)] disabled:opacity-50"
              >
                <option value={0}>Aucun</option>
                <option value={5}>5 min</option>
                <option value={10}>10 min</option>
                <option value={15}>15 min</option>
                <option value={60}>1 h</option>
              </select>
            </label>
          </div>
          {members.length > 0 ? (
            <div className="space-y-1">
              <p className="text-xs text-[var(--chat-muted)]">Participants Suite</p>
              <p className="text-[11px] leading-relaxed text-[var(--chat-muted)]">
                Seules les personnes cochées reçoivent l’agenda et le mail. Décochez pour ne pas
                les convier — les retirer de la salle enlève aussi leur événement.
              </p>
              <div className="max-h-40 space-y-0.5 overflow-y-auto rounded-xl border border-[var(--chat-border)] p-1">
                {members.map((m) => {
                  const checked = draftAttendees.includes(m.id);
                  const isSelf = m.id === selfId;
                  return (
                    <label
                      key={m.id}
                      className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm ${
                        isSelf ? "opacity-80" : "cursor-pointer hover:bg-[var(--chat-surface-elevated)]"
                      } ${checked ? "bg-[var(--chat-accent-soft)]" : ""}`}
                    >
                      <input
                        type="checkbox"
                        className="size-3.5 accent-[var(--chat-accent)]"
                        checked={checked}
                        disabled={locked || isSelf}
                        onChange={() => {
                          if (isSelf) return;
                          setDraftAttendees((prev) =>
                            checked ? prev.filter((id) => id !== m.id) : [...prev, m.id]
                          );
                        }}
                      />
                      <span className="min-w-0 flex-1 truncate">
                        {m.name}
                        {isSelf ? " (vous)" : ""}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          ) : null}
          <button
            type="button"
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-[var(--chat-accent-soft)] px-3 py-2 text-xs font-medium text-[var(--chat-accent-strong)] disabled:opacity-50"
            disabled={locked}
            onClick={() => void submit()}
          >
            {saving ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : null}
            {saving ? "Enregistrement…" : meeting ? "Enregistrer (heure et titre)" : "Planifier"}
          </button>
        </div>
      ) : null}

      {meeting ? (
        <div className="space-y-2 border-t border-[var(--chat-border)] pt-3">
          <p className="text-xs font-semibold text-[var(--chat-muted)]">Invités de ce créneau</p>
          <div className="flex gap-1.5">
            <input
              type="email"
              value={guestEmail}
              onChange={(e) => setGuestEmail(e.target.value)}
              placeholder="e-mail de l’invité"
              disabled={locked}
              className="min-w-0 flex-1 rounded-lg border border-[var(--chat-border)] bg-[var(--chat-bg)] px-2.5 py-1.5 text-sm outline-none focus:border-[var(--chat-accent)]"
            />
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-lg bg-[var(--chat-accent-soft)] px-2.5 py-1.5 text-xs font-medium text-[var(--chat-accent-strong)] disabled:opacity-50"
              disabled={locked}
              onClick={() => void inviteGuest()}
            >
              {guestBusy ? <Loader2 className="size-3 animate-spin" /> : <UserPlus className="size-3" />}
              Inviter
            </button>
            <button
              type="button"
              className="inline-flex items-center rounded-lg border border-[var(--chat-border)] px-2.5 py-1.5 text-xs font-medium disabled:opacity-50"
              disabled={locked}
              onClick={() => void copyMeetingLink()}
            >
              Lien
            </button>
          </div>
          <ChatGuestRows
            guests={guests.filter((g) => g.meeting_id === meeting.id)}
            locked={locked}
            revokeBusyId={revokeBusyId}
            reinviteBusyId={reinviteBusyId}
            onRevoke={(id) => void revokeGuest(id)}
            onReinvite={(g) => void relaunchGuest(g.id)}
          />
        </div>
      ) : null}

      {hint ? <p className="text-xs text-[var(--chat-muted)]">{hint}</p> : null}
    </div>
  );
}
